import { useTranslation } from "react-i18next";
import { Label, NumberInput, Button } from "@tellescope/ui";
import { useForm } from "@tellescope/forms";
import type { NutritionTargets } from "./useWeightMacroTrackerBlock";
import { mlToOz, ozToMl } from "../../../utils/nutritionUnitConversions";

interface NutritionTargetsEditorProps {
  targets: NutritionTargets;
  onSave: (targets: NutritionTargets) => Promise<unknown>;
  isPending: boolean;
  onClose: () => void;
}

export const NutritionTargetsEditor = ({ targets, onSave, isPending, onClose }: NutritionTargetsEditorProps) => {
  const { t } = useTranslation("portal");

  const form = useForm({
    fields: {
      calories: { type: "number", initialValue: targets.dailyCalorieTarget ?? 0 },
      protein: { type: "number", initialValue: targets.dailyProteinTarget ?? 0 },
      carbs: { type: "number", initialValue: targets.dailyCarbTarget ?? 0 },
      fat: { type: "number", initialValue: targets.dailyFatTarget ?? 0 },
      fiber: { type: "number", initialValue: targets.dailyFiberTarget ?? 0 },
      waterOz: { type: "number", initialValue: targets.dailyWaterTarget ? mlToOz(targets.dailyWaterTarget) : 0 },
    },
  });

  const { calories, protein, carbs, fat, fiber, waterOz } = form.values;

  const numberFieldProps = (field: "calories" | "protein" | "carbs" | "fat" | "fiber" | "waterOz") => {
    const { onValueChange, ...rest } = form.getCustomFieldProps(field);
    return { ...rest, onChange: (newValue: number | undefined) => onValueChange(newValue ?? 0) };
  };

  const handleSave = async () => {
    await onSave({
      dailyCalorieTarget: calories,
      dailyProteinTarget: protein,
      dailyCarbTarget: carbs,
      dailyFatTarget: fat,
      dailyFiberTarget: fiber,
      dailyWaterTarget: waterOz !== 0 ? ozToMl(waterOz) : undefined,
    });
    onClose();
  };

  return (
    <div className="space-y-3 rounded-lg border bg-card p-4">
      <p className="text-sm font-medium">{t("Edit targets")}</p>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>{t("Calories")}</Label>
          <NumberInput {...numberFieldProps("calories")} precisionOptions={{ maxDecimalDigitCount: 0 }} />
        </div>
        <div className="space-y-1.5">
          <Label>{t("Protein (g)")}</Label>
          <NumberInput {...numberFieldProps("protein")} precisionOptions={{ maxDecimalDigitCount: 0 }} />
        </div>
        <div className="space-y-1.5">
          <Label>{t("Carbs (g)")}</Label>
          <NumberInput {...numberFieldProps("carbs")} precisionOptions={{ maxDecimalDigitCount: 0 }} />
        </div>
        <div className="space-y-1.5">
          <Label>{t("Fat (g)")}</Label>
          <NumberInput {...numberFieldProps("fat")} precisionOptions={{ maxDecimalDigitCount: 0 }} />
        </div>
        <div className="space-y-1.5">
          <Label>{t("Fiber (g)")}</Label>
          <NumberInput {...numberFieldProps("fiber")} precisionOptions={{ maxDecimalDigitCount: 0 }} />
        </div>
        <div className="space-y-1.5">
          <Label>{t("Water (oz)")}</Label>
          <NumberInput {...numberFieldProps("waterOz")} precisionOptions={{ maxDecimalDigitCount: 0 }} />
        </div>
      </div>
      <div className="flex gap-2">
        <Button onClick={handleSave} disabled={isPending}>
          {t("Save")}
        </Button>
        <Button variant="ghost" onClick={onClose} disabled={isPending}>
          {t("Cancel")}
        </Button>
      </div>
    </div>
  );
};
